// src/TeamDashboard.js
import { useState, useEffect, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AuthContext } from "./App";
import GameStatsTables from "./GameStatsTables";
import PlayerStats from "./PlayerStats";
import AdvancedPlayerStats from "./AdvancedStats";
import GamesComponent from "./Games";
import logo from "./logo/Logo-Viktoria-Mitte-3.png";
import "./index.css";

function TeamDashboard() {
  const { accessToken, logout, teamId: authTeamId } = useContext(AuthContext);
  const navigate = useNavigate();
  const { teamId: routeTeamId } = useParams();

  // Use routeTeamId first, fallback to authTeamId
  const finalTeamId = routeTeamId ? Number(routeTeamId) : Number(authTeamId);

  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [games, setGames] = useState([]);
  const [stats, setStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("stats");
  const [error, setError] = useState("");

  const API = process.env.REACT_APP_API_URL;

  // Helper for authenticated fetch
  const fetchWithAuth = async (endpoint) => {
    const res = await fetch(`${API}${endpoint}`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    if (res.status === 401) {
      logout();
      navigate("/login");
      return [];
    }

    return res.json().catch(() => []);
  };

  useEffect(() => {
    if (!finalTeamId) {
      setError("No team assigned.");
      setLoading(false);
      return;
    }

    const loadData = async () => {
      setLoading(true);
      try {
        const teamData = await fetchWithAuth(`/teams/${finalTeamId}`);
        setTeam(teamData);

        const playersData = await fetchWithAuth(`/players?team_id=${finalTeamId}`);
        setPlayers(playersData);

        const gamesData = await fetchWithAuth(`/games?team_id=${finalTeamId}`);
        setGames(gamesData);

        const allStats = await fetchWithAuth("/stats");
        const teamPlayerIds = playersData.map((p) => p.id);
        const filteredStats = allStats.filter((s) => teamPlayerIds.includes(s.player_id));
        setStats(filteredStats);
      } catch (err) {
        console.error(err);
        setError("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [routeTeamId, authTeamId]);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // ✅ New handler for tabs
  const handleTabClick = (key) => {
    if (key === "logout") {
      handleLogout();
    } else if (key === "switch") {
      navigate("/select-team");
    } else {
      setActiveTab(key);
    }
  };

  const selectedTeamName =
    (team && team.name) ||
    (players && players.length && players[0].team_name) ||
    "Team";

  if (loading) return <p className="text-center mt-10">Loading…</p>;
  if (error) return <p className="text-center mt-10 text-red-500">{error}</p>;

  return (
    <div className="p-6 bg-white rounded-2xl shadow-md w-full max-w-5xl mx-auto mt-8 relative">

      {/* Header */}
      <header className="bg-blue-800 text-white py-6 shadow-md rounded-b-2xl">
        <div className="flex flex-col items-center space-y-4">
          <img src={logo} alt="Logo" className="h-20 w-auto" />
          <h1 className="text-3xl font-bold text-center">
            {selectedTeamName} Dashboard
          </h1>
        </div>
      </header>

      {/* Tabs */}
      <div className="flex space-x-4 mb-4 mt-4">
        {[
          { key: "stats", label: "Game Stats" },
          { key: "players", label: "Player Stats" },
          { key: "advanced", label: "Advanced Stats" },
          { key: "games", label: "Schedule" },
          { key: "switch", label: "Switch Team" },
          { key: "logout", label: "Logout", logout: true },
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => handleTabClick(key)}
            className={`px-4 py-2 rounded-lg font-semibold ${
              activeTab === key ? "bg-blue-600 text-white" : "bg-gray-200 text-gray-800"
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      {activeTab === "stats" && (
        <GameStatsTables stats={stats} games={games} teamId={finalTeamId} />
      )}
      {activeTab === "players" && (
        <PlayerStats stats={stats} players={players} />
      )}
      {activeTab === "advanced" && (
        <AdvancedPlayerStats stats={stats} players={players} teamId={finalTeamId} />
      )}
      {activeTab === "games" && (
        <GamesComponent gamesList={games} teamId={finalTeamId} />
      )}
      {/* "Switch Team" does not render a component; it just navigates to /select-team */}

    </div>
  );
}

export default TeamDashboard;
